import { useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ETAPES, ETAPE_INFO, foyerManuelSchema, nomDeFoyer, type Etape, type NombreFoyer } from '@dinertournant/core';
import { ErreurApi, api, type FoyerOrganisateur } from '../lib/api.js';
import { BandeauAlerte } from './BandeauAlerte.js';
import { Champ } from './Champ.js';

interface Valeurs {
  nom: string;
  nombre: NombreFoyer;
  personne1: string;
  regime1?: string;
  personne2?: string;
  regime2?: string;
  personne3?: string;
  regime3?: string;
  email1?: string;
  email2?: string;
  email3?: string;
  participe1: boolean;
  participe2: boolean;
  participe3: boolean;
  telephone?: string;
  adresse?: string;
  complementAdresse?: string;
  veutRecevoir: boolean;
  disponibilites: Etape[];
  capaciteMax?: number | undefined;
  /** À l'ajout seulement : envoyer la confirmation d'inscription par e-mail. */
  prevenir: boolean;
}

function versApi(v: Valeurs) {
  const vide = (s: string | undefined) => (s && s.trim() ? s.trim() : undefined);
  const membres = [{ prenomNom: v.personne1.trim(), participe: v.participe1, regime: vide(v.regime1), email: vide(v.email1) }];
  if (v.nombre >= 2) membres.push({ prenomNom: (v.personne2 ?? '').trim(), participe: v.participe2, regime: vide(v.regime2), email: vide(v.email2) });
  if (v.nombre >= 3) membres.push({ prenomNom: (v.personne3 ?? '').trim(), participe: v.participe3, regime: vide(v.regime3), email: vide(v.email3) });
  const nom = vide(v.nom) ?? nomDeFoyer(membres.map((m) => m.prenomNom));
  return {
    nom,
    telephone: vide(v.telephone),
    adresse: vide(v.adresse),
    complementAdresse: vide(v.complementAdresse),
    veutRecevoir: v.veutRecevoir,
    disponibilites: v.veutRecevoir ? v.disponibilites : [],
    capaciteMax: v.veutRecevoir && v.capaciteMax ? Number(v.capaciteMax) : undefined,
    membres,
    prevenir: !!v.prevenir,
  };
}

function depuis(f: FoyerOrganisateur): Valeurs {
  const [m1, m2, m3] = f.membres;
  return {
    nom: f.nom,
    nombre: Math.min(3, Math.max(1, f.membres.length)) as NombreFoyer,
    personne1: m1?.prenomNom ?? '',
    regime1: m1?.regime ?? '',
    personne2: m2?.prenomNom ?? '',
    regime2: m2?.regime ?? '',
    personne3: m3?.prenomNom ?? '',
    regime3: m3?.regime ?? '',
    // Anciens foyers (import CSV) : l'e-mail de contact devient celui de la 1re personne.
    email1: m1?.email ?? (f.membres.some((m) => m.email) ? '' : (f.email ?? '')),
    email2: m2?.email ?? '',
    email3: m3?.email ?? '',
    participe1: m1?.participe ?? true,
    participe2: m2?.participe ?? true,
    participe3: m3?.participe ?? true,
    telephone: f.telephone ?? '',
    adresse: f.adresse ?? '',
    complementAdresse: f.complementAdresse ?? '',
    veutRecevoir: f.veutRecevoir,
    disponibilites: f.disponibilites,
    capaciteMax: f.capaciteMax ?? 6,
    prevenir: false,
  };
}

interface Props {
  eventId: string;
  foyer?: FoyerOrganisateur | undefined;
  /** `confirmations` : nombre d'e-mails de confirmation mis en file (ajout seulement). */
  onEnregistre: (f: FoyerOrganisateur, confirmations?: number) => void;
  onAnnuler: () => void;
}

/** Ajout ou modification d'un foyer par l'organisateur (source « manuel »). */
export function FoyerForm({ eventId, foyer, onEnregistre, onAnnuler }: Props) {
  const [erreur, setErreur] = useState<string | null>(null);
  const { register, handleSubmit, watch, setValue, formState } = useForm<Valeurs>({
    resolver: ((v: Valeurs, c: unknown, o: unknown) => (zodResolver(foyerManuelSchema) as unknown as (a: unknown, b: unknown, d: unknown) => unknown)(versApi(v), c, o)) as Resolver<Valeurs>,
    defaultValues: foyer ? depuis(foyer) : { nom: '', nombre: 1, personne1: '', personne2: '', personne3: '', email1: '', email2: '', email3: '', participe1: true, participe2: true, participe3: true, telephone: '', adresse: '', complementAdresse: '', veutRecevoir: false, disponibilites: [], capaciteMax: 6, prevenir: true },
  });
  const nombre = watch('nombre');
  const veutRecevoir = watch('veutRecevoir');
  const erreurs = formState.errors as Record<string, { message?: string } | undefined>;
  const msg = (k: string) => erreurs[k]?.message ?? erreurs[`membres.${Number(k.slice(-1)) - 1}.${k.startsWith('email') ? 'email' : 'prenomNom'}`]?.message;

  // Le resolver valide `versApi(valeurs)` : handleSubmit reçoit donc la sortie du schéma, pas les champs bruts.
  const onSubmit = handleSubmit(async (brut) => {
    setErreur(null);
    const corps = brut as unknown as ReturnType<typeof versApi>;
    try {
      if (foyer) {
        const r = await api.put<{ foyer: FoyerOrganisateur; hotesRetires?: string[] }>(`/api/events/${eventId}/households/${foyer.id}`, corps);
        onEnregistre(r.foyer);
      } else {
        const r = await api.post<{ foyer: FoyerOrganisateur; confirmations?: number }>(`/api/events/${eventId}/households`, corps);
        onEnregistre(r.foyer, r.confirmations ?? 0);
      }
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Impossible de joindre le serveur.');
    }
  });

  return (
    <form className="pile" onSubmit={onSubmit} noValidate>
      {erreur ? <BandeauAlerte niveau="attention" role="alert" titre={erreur} /> : null}
      <div className="segments" role="group" aria-label="Nombre de personnes">
        {([1, 2, 3] as const).map((n) => (
          <button key={n} type="button" className="seg" aria-pressed={nombre === n} onClick={() => setValue('nombre', n)}>
            {n === 1 ? 'Une personne' : n === 2 ? 'Deux personnes' : 'Trois personnes'}
          </button>
        ))}
      </div>
      <div className="grille-3">
        <Champ id="f-personne1" label="Prénom et nom" erreur={msg('personne1')} {...register('personne1')} />
        <Champ id="f-email1" label="E-mail" optionnel="reçoit le parcours" type="email" erreur={msg('email1')} {...register('email1')} />
        <Champ id="f-regime1" label="Régime" optionnel="facultatif" placeholder="sans gluten" {...register('regime1')} />
      </div>
      {nombre >= 2 ? (
        <div className="grille-3">
          <Champ id="f-personne2" label="Prénom et nom de la 2e personne" erreur={msg('personne2')} {...register('personne2')} />
          <Champ id="f-email2" label="E-mail" optionnel="facultatif" type="email" erreur={msg('email2')} {...register('email2')} />
          <Champ id="f-regime2" label="Régime" optionnel="facultatif" {...register('regime2')} />
        </div>
      ) : null}
      {nombre >= 3 ? (
        <div className="grille-3">
          <Champ id="f-personne3" label="Prénom et nom de la 3e personne" erreur={msg('personne3')} {...register('personne3')} />
          <Champ id="f-email3" label="E-mail" optionnel="facultatif" type="email" erreur={msg('email3')} {...register('email3')} />
          <Champ id="f-regime3" label="Régime" optionnel="facultatif" {...register('regime3')} />
        </div>
      ) : null}
      {foyer ? (
        <div className="ligne" style={{ gap: 18, flexWrap: 'wrap' }}>
          <label className="ligne" style={{ gap: 8 }}>
            <input type="checkbox" {...register('participe1')} /> {watch('personne1') || 'Personne 1'} participe
          </label>
          {nombre >= 2 ? (
            <label className="ligne" style={{ gap: 8 }}>
              <input type="checkbox" {...register('participe2')} /> {watch('personne2') || 'Personne 2'} participe
            </label>
          ) : null}
          {nombre >= 3 ? (
            <label className="ligne" style={{ gap: 8 }}>
              <input type="checkbox" {...register('participe3')} /> {watch('personne3') || 'Personne 3'} participe
            </label>
          ) : null}
        </div>
      ) : null}
      <Champ id="f-nom" label="Nom de la voiture" optionnel="vide = déduit des noms" placeholder="les Martin" {...register('nom')} />
      <Champ id="f-telephone" label="Téléphone" optionnel="facultatif" type="tel" {...register('telephone')} />
      <Champ id="f-adresse" label="Adresse" optionnel="obligatoire pour recevoir" {...register('adresse')} />
      <Champ id="f-complement" label="Complément d'adresse" optionnel="étage, code…" {...register('complementAdresse')} />
      <fieldset className="bloc-doux" style={{ border: 0, margin: 0 }}>
        <legend className="f-label" style={{ padding: 0 }}>
          Accepte de recevoir ?
        </legend>
        <div className="segments" role="group" aria-label="Recevoir">
          <button type="button" className="seg" aria-pressed={veutRecevoir} onClick={() => setValue('veutRecevoir', true)}>
            Oui
          </button>
          <button type="button" className="seg" aria-pressed={!veutRecevoir} onClick={() => setValue('veutRecevoir', false)}>
            Non
          </button>
        </div>
        {veutRecevoir ? (
          <div className="ligne" style={{ gap: 16, flexWrap: 'wrap' }}>
            {ETAPES.map((e) => (
              <label key={e} className="ligne" style={{ gap: 8 }}>
                <input type="checkbox" value={e} {...register('disponibilites')} /> {ETAPE_INFO[e].label}
              </label>
            ))}
            {erreurs.disponibilites?.message ? <span className="f-erreur">{erreurs.disponibilites.message}</span> : null}
            <label className="ligne" style={{ gap: 8 }}>
              <span>Jusqu'à</span>
              <select className="f-input" style={{ width: 'auto', minHeight: 40, padding: '6px 10px' }} aria-label="Capacité maximale, hôtes compris" {...register('capaciteMax', { setValueAs: (v) => (v === '' ? undefined : Number(v)) })}>
                {[3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span>personnes à table, hôtes compris</span>
            </label>
          </div>
        ) : null}
      </fieldset>
      {!foyer ? (
        <label className="ligne" style={{ gap: 8, alignItems: 'flex-start' }}>
          <input type="checkbox" {...register('prevenir')} />
          <span>
            Prévenir par e-mail
            <span className="f-hint" style={{ display: 'block' }}>
              Chaque personne qui a un e-mail reçoit la confirmation d'inscription, avec un lien pour vérifier et corriger ses informations.
            </span>
          </span>
        </label>
      ) : null}
      <div className="ligne" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={onAnnuler}>
          Annuler
        </button>
        <button type="submit" className="btn principal" disabled={formState.isSubmitting}>
          {foyer ? 'Enregistrer' : 'Ajouter la voiture'}
        </button>
      </div>
    </form>
  );
}
